/**
 * Date-fns locales for date pickers, following the user's UI language
 */

import { registerLocale } from "react-datepicker";
import type { Locale as DateFnsLocale } from "date-fns";
import enGB from "date-fns/locale/en-GB";
import fi from "date-fns/locale/fi";
import ru from "date-fns/locale/ru";
import sv from "date-fns/locale/sv";
import { getLocale, gettext, Locale } from "i18n";

// Registered under "en-GB", not "en": react-datepicker formats some parts of
// a locale named "en" with its built-in US English (12-hour times). British
// English also keeps Monday as the first day of the week.
const DATE_PICKER_LOCALES: Record<string, DateFnsLocale> = {
  "en-GB": enGB,
  fi,
  sv,
  ru,
};

const PICKER_LOCALE_BY_LANGUAGE: Record<Locale, string> = {
  en: "en-GB",
  fi: "fi",
  sv: "sv",
  ru: "ru",
  xx: "en-GB",
};

Object.entries(DATE_PICKER_LOCALES).forEach(([name, locale]) =>
  registerLocale(name, locale),
);

/**
 * Name of the registered react-datepicker locale for the current UI language,
 * for the `locale` prop of DatePicker. The UI locale is a language ("fi") or
 * a language tag with a region ("fi-FI", or "fi_FI" before gettext.js
 * normalizes it).
 */
export const getDatePickerLocale = (): string => {
  const language = String(getLocale() ?? "")
    .toLowerCase()
    .split(/[-_]/)[0];
  return PICKER_LOCALE_BY_LANGUAGE[language as Locale] ?? "en-GB";
};

/**
 * Locale and the texts react-datepicker does not take from the locale, for
 * spreading into a DatePicker: `<DatePicker {...getDatePickerLocaleProps()} />`
 */
export const getDatePickerLocaleProps = () => {
  const previousMonth = gettext("Previous month");
  const nextMonth = gettext("Next month");
  return {
    locale: getDatePickerLocale(),
    timeCaption: gettext("Time"),
    previousMonthAriaLabel: previousMonth,
    previousMonthButtonLabel: previousMonth,
    nextMonthAriaLabel: nextMonth,
    nextMonthButtonLabel: nextMonth,
  };
};
